/**
 * useFlightSimulator (#441 Phase 4, #451) — starts, drives and ends a
 * simulated leg on the tracking view.
 *
 * The leg is GIVEN to `startSimulation`, never read from a briefing. It
 * used to come from `notamStore.flightRoute`, and the only way off the
 * briefing screen clears that route (`ResultsView.goBack`), so the single
 * path to the simulator destroyed what the simulator needed (#451).
 *
 * It owns nothing about geometry or timing: the `SimulatedPositionProvider`
 * does that. This composable is the wiring — it swaps the tracking loop's
 * position source, restarts the loop, mirrors the provider's state into
 * refs the views can render, and guarantees the session ends cleanly.
 *
 * Module-level singleton, like `useContextualNotams`: there is one
 * tracking loop, so there is one simulated session. All callers share it.
 *
 * There is no pause. It went with the in-flight strip in #478 — a flying
 * leg leaves two controls, the rate and End — and this is the record of
 * that being deliberate rather than dropped: `_isPaused` is still mirrored
 * because `SimulatedPositionProvider` still has the concept, but nothing
 * in the client can reach it.
 *
 * Progress is mirrored off the store's position updates rather than a
 * timer of its own. The provider already emits a fix every tick, so a
 * second interval would be a second clock to keep in sync for nothing.
 */
import { computed, ref, shallowRef, watch } from 'vue';
import { config } from '@/config/config';
import {
  positionProviderKind,
  useContextualNotams
} from '@/composables/useContextualNotams';
import { useGeolocationPermission } from '@/composables/useGeolocationPermission';
import { trackingArmed } from '@/composables/useTrackingArm';
import { trackingStartMode } from '@/utils/trackingStartPolicy';
import { useContextualStore } from '@/stores/contextualStore';
import { SimulatedPositionProvider } from '@/services/position/SimulatedPositionProvider';
import { isDegenerateLeg, isValidLatLon, type LatLon } from '@/utils/greatCircle';
import type { FlightRoute, FlightRoutePoint } from '@/types/notam';

/**
 * Why a leg did or did not start. A boolean made the caller report one
 * cause as another: a leg the tracking loop declined to arm was shown as
 * "those airports did not come back with usable coordinates", which is
 * false and unactionable. Every refusal a pilot can see has its own name.
 */
export type SimulatorStart =
  | 'started'
  /** A leg is already flying; the caller has nothing to report. */
  | 'already-flying'
  /** At least one airport's coordinates are not coordinates. */
  | 'bad-coords'
  /** Same point, or antipodal — either way there is no great circle to fly. */
  | 'unflyable-leg'
  /** The tracking loop refused to arm; nothing is flying and GPS is back. */
  | 'not-armed';

// --- Singleton state ----------------------------------------------------
/**
 * Whether a simulated leg is flying is NOT stored here. It is derived from
 * the tracking loop's own provider, because a second flag drifts: leaving
 * /tracking made App.vue stop the loop, which reverted the provider but
 * left the flag set, and a real GPS fix then came back labelled "this is
 * not your real position" with a hollow ownship. One truth, no drift.
 */
const _isSimulating = computed(() => positionProviderKind.value === 'simulated');

/**
 * Whether a simulated leg is flying. Exported on its own because most
 * readers want ONLY this: the aircraft layer, the tracking view and
 * App.vue's start policy each read one boolean, and calling the session
 * factory for it resolves four stores and builds a dozen computeds per
 * component instance.
 */
export const isSimulating = _isSimulating;
const _provider = shallowRef<SimulatedPositionProvider | null>(null);
const _progress = ref(0);
const _isPaused = ref(false);
const _isFinished = ref(false);
const _groundSpeedKt = ref(config.simulator.defaultGroundSpeedKt);
const _altitudeFt = ref(config.simulator.defaultAltitudeFt);
const _timeAcceleration = ref(config.simulator.defaultTimeAcceleration);
const _maxAcceleration = ref(config.simulator.maxTimeAcceleration);
const _legLabel = ref('');
let _unwatchPosition: (() => void) | null = null;

/**
 * `FlightRoutePoint.coords` is GeoJSON `[lng, lat]` — the opposite order
 * to everything downstream. Converting in exactly one place is what keeps
 * a simulated leg from flying to the transpose of its destination.
 */
function _toLatLon(point: FlightRoutePoint | undefined): LatLon | null {
  if (!point || !Array.isArray(point.coords) || point.coords.length < 2) return null;
  const candidate = { lat: point.coords[1], lon: point.coords[0] };
  return isValidLatLon(candidate) ? candidate : null;
}

function _mirrorProviderState(): void {
  const provider = _provider.value;
  if (!provider) return;
  _progress.value = provider.progress;
  _isPaused.value = provider.isPaused;
  _isFinished.value = provider.isFinished;
  _groundSpeedKt.value = provider.groundSpeedKt;
  _altitudeFt.value = provider.altitudeFt;
  _timeAcceleration.value = provider.timeAcceleration;
  _maxAcceleration.value = provider.maxAllowedAcceleration;
}

/** Reject a non-number outright; otherwise hold it inside the range. */
function _clampToRange(value: number, min: number, max: number): number | null {
  if (!Number.isFinite(value)) return null;
  return Math.min(max, Math.max(min, value));
}

/** Drop everything that belongs to a session. Safe to call twice. */
function _endSession(): void {
  if (_unwatchPosition !== null) {
    _unwatchPosition();
    _unwatchPosition = null;
  }
  _provider.value = null;
  _progress.value = 0;
  _isPaused.value = false;
  _isFinished.value = false;
  _legLabel.value = '';
  _maxAcceleration.value = config.simulator.maxTimeAcceleration;
  // A ceiling reached mid-leg can leave the multiplier below the default;
  // carrying that into the next leg would silently start it slowed down.
  _timeAcceleration.value = config.simulator.defaultTimeAcceleration;
}

/**
 * The loop can leave the simulated source without going through
 * `stopSimulation` — App.vue stops tracking on every route change. When
 * that happens the session state has to go with it, or the controls keep
 * poking a provider that is no longer driving anything.
 */
watch(
  positionProviderKind,
  (kind) => {
    if (kind !== 'simulated') _endSession();
  },
  // SYNC on purpose. A default 'pre' watcher flushes on the microtask
  // queue and compares against the last FLUSHED value, so a start
  // immediately followed by a stop in the same tick looks like no change
  // at all and the session is never ended. The kind changes a handful of
  // times per session, so there is nothing to batch.
  { flush: 'sync' }
);

export function useFlightSimulator() {
  const contextualStore = useContextualStore();
  const { start, stop, setPositionProvider, isRunning } = useContextualNotams();

  /**
   * Begin a simulated leg over the given route, and say what happened. The
   * caller keeps whatever it was showing on anything but `'started'`,
   * rather than entering a half-started state.
   *
   * The coordinate guards below are a FLOOR, not the user-facing
   * validation, but they are NOT redundant: `SimulatedPositionProvider`
   * refuses the same legs, and reaching it means the real receiver has
   * already been torn down and re-armed for a leg we could tell was
   * unflyable before touching it.
   */
  function startSimulation(route: FlightRoute): SimulatorStart {
    // Re-entry would replace `_unwatchPosition` and leak the previous
    // watcher: it is created in a click handler, not a component setup, so
    // there is no effect scope to dispose it for us.
    if (_isSimulating.value) return 'already-flying';
    const from = _toLatLon(route.departure);
    const to = _toLatLon(route.destination);
    if (from === null || to === null) return 'bad-coords';
    if (isDegenerateLeg(from, to)) return 'unflyable-leg';

    const provider = new SimulatedPositionProvider({
      from,
      to,
      groundSpeedKt: _groundSpeedKt.value,
      altitudeFt: _altitudeFt.value,
      timeAcceleration: _timeAcceleration.value
    });

    // Swapping stops the loop and resets the store, so no real fix
    // survives into the simulated session. The loop is then started
    // explicitly — never silently, per #441's threat model.
    setPositionProvider(provider);
    _provider.value = provider;
    start();

    // The loop can decline to arm. Reporting success then would give the
    // pilot the whole "flying" UI over a dead view, with BOTH the
    // simulated source and the real receiver stopped.
    if (!isRunning.value) {
      _endSession();
      stop();
      _rearmRealReceiver();
      return 'not-armed';
    }

    _legLabel.value = `${route.departure.code} → ${route.destination.code}`;
    _progress.value = 0;
    _isPaused.value = false;
    _isFinished.value = false;
    _mirrorProviderState();
    // The provider emits a fix per tick; mirroring off that is one clock
    // instead of two.
    _unwatchPosition = watch(() => contextualStore.lastPosition, _mirrorProviderState);
    return 'started';
  }

  /**
   * Put the browser's receiver back to work.
   *
   * No PERMISSION check here: `GeolocationPositionProvider.start()` already
   * refuses on 'denied' and reports it, so a second one was a copy of a
   * policy that lives a level down — and, being unobservable, a guard no
   * test could discriminate.
   *
   * The `'geolocation' in navigator` check is NOT the same kind of thing
   * and has to stay. `setRuntimePermissionDenied()` has three effects, and
   * #449 removed only two of them: the tab no longer hides and the pilot is
   * no longer ejected, but the singleton still latches 'denied' for the
   * whole session with no path back — the Permissions API 'change' event
   * only fires on a real permission change. So arming a receiver on a
   * browser that has no `navigator.geolocation` (a backgrounded WebView
   * tears it down; that is what `_unreleasedWatchId` exists for) would kill
   * REAL GPS until a page reload, and tell the pilot their location
   * permission was denied, which is false. Tracked as #446.
   */
  function _rearmRealReceiver(): void {
    if (!('geolocation' in navigator)) return;
    // #525: the same start policy App.vue applies — a real receiver on a
    // 'prompt' permission waits for the tap, and ending a simulated leg is
    // not that tap. Without this, Stop fired the location dialog directly.
    // Anything but 'real' is App.vue's to handle: its watcher sees the leg
    // end and arms the approximate source (#527) or stops.
    const { permissionState } = useGeolocationPermission();
    if (trackingStartMode('tracking', permissionState.value, false, trackingArmed.value) !== 'real') return;
    start();
  }

  /** End the simulated leg and hand the loop back to the real receiver. */
  function stopSimulation(): void {
    // stop() reverts the provider to GeolocationPositionProvider, which is
    // what prevents a simulated leg from resuming on the next /tracking
    // entry with no user action. The kind watcher then ends the session.
    stop();
    _endSession();
    _rearmRealReceiver();
  }

  // Every setter READS BACK what the provider accepted rather than storing
  // what was asked for. The provider rejects a non-positive speed and caps
  // the acceleration, so mirroring the request would put a number on the
  // control that the aircraft is not flying — and seed the next leg with
  // it.
  function setGroundSpeedKt(kt: number): void {
    // The range belongs to the setter that owns the value, not to the one
    // component that happens to call it. These refs are module-level and
    // seed the NEXT leg, so an out-of-range value written while idle is
    // sticky — it would be handed straight to the provider constructor,
    // and the API budget ceiling computed from it.
    const clamped = _clampToRange(
      kt,
      config.simulator.minGroundSpeedKt,
      config.simulator.maxGroundSpeedKt
    );
    if (clamped === null) return;
    const provider = _provider.value;
    if (provider === null) {
      _groundSpeedKt.value = clamped;
      return;
    }
    provider.setGroundSpeedKt(clamped);
    _mirrorProviderState();
  }

  function setAltitudeFt(ft: number): void {
    const clamped = _clampToRange(
      ft,
      config.simulator.minAltitudeFt,
      config.simulator.maxAltitudeFt
    );
    if (clamped === null) return;
    const provider = _provider.value;
    if (provider === null) {
      _altitudeFt.value = clamped;
      return;
    }
    provider.setAltitudeFt(clamped);
    _mirrorProviderState();
  }

  function setTimeAcceleration(multiplier: number): void {
    const provider = _provider.value;
    if (provider === null) return;
    provider.setTimeAcceleration(multiplier);
    _mirrorProviderState();
  }

  /**
   * The multipliers the control may offer right now. Filtered by what the
   * provider will actually accept, so a config change to
   * `accelerationSteps` or `maxTimeAcceleration` can never leave the
   * control stuck on a value it can no longer cycle out of — and a high
   * ground speed simply removes the steps that would breach the API
   * budget.
   */
  const accelerationSteps = computed(() => {
    // Reads the MIRRORED ceiling, not `provider.maxAllowedAcceleration`:
    // that is a getter on a plain class instance, so a computed cannot
    // track it and the list stayed stale after a speed change — offering a
    // step that breached the budget.
    const allowed = config.simulator.accelerationSteps.filter(
      (step) => step <= _maxAcceleration.value
    );
    return allowed.length > 0 ? allowed : [1];
  });

  return {
    isSimulating: _isSimulating,
    accelerationSteps,
    isFinished: computed(() => _isFinished.value),
    progress: computed(() => _progress.value),
    groundSpeedKt: computed(() => _groundSpeedKt.value),
    altitudeFt: computed(() => _altitudeFt.value),
    timeAcceleration: computed(() => _timeAcceleration.value),
    legLabel: computed(() => _legLabel.value),
    startSimulation,
    stopSimulation,
    setGroundSpeedKt,
    setAltitudeFt,
    setTimeAcceleration
  };
}

// --- Test hooks --------------------------------------------------------
export function _resetSimulatorForTest(): void {
  _provider.value?.stop();
  _endSession();
  _groundSpeedKt.value = config.simulator.defaultGroundSpeedKt;
  _altitudeFt.value = config.simulator.defaultAltitudeFt;
  _timeAcceleration.value = config.simulator.defaultTimeAcceleration;
  _maxAcceleration.value = config.simulator.maxTimeAcceleration;
}
